import React, { useEffect, useRef, useState } from 'react';

export const WHATSAPP_NUMBER = '918287651258';
export const WHATSAPP_DISPLAY = '+91 82876 51258';

export const WhatsAppIcon = ({ className = 'h-6 w-6' }) => (
  <svg viewBox="0 0 32 32" fill="currentColor" className={className} aria-hidden="true">
    <path d="M16.004 3C9.38 3 3.99 8.39 3.99 15.014c0 2.12.555 4.19 1.61 6.015L3.9 27.9l7.05-1.65a12 12 0 0 0 5.053 1.12h.005c6.623 0 12.013-5.39 12.013-12.014C28.02 8.39 22.63 3 16.004 3Zm0 21.96h-.004a9.94 9.94 0 0 1-5.07-1.39l-.364-.216-4.183.98 1.003-4.08-.237-.377a9.92 9.92 0 0 1-1.52-5.264c0-5.49 4.468-9.957 9.962-9.957 5.49 0 9.957 4.467 9.957 9.957 0 5.49-4.467 9.958-9.957 9.958Zm5.46-7.456c-.299-.15-1.77-.873-2.044-.973-.274-.1-.474-.15-.673.15-.2.3-.773.973-.947 1.172-.175.2-.35.225-.648.075-.3-.15-1.264-.466-2.408-1.486-.89-.794-1.49-1.774-1.665-2.074-.175-.3-.019-.461.131-.61.135-.134.3-.35.449-.524.15-.175.2-.3.3-.5.1-.2.05-.374-.025-.524-.075-.15-.673-1.623-.922-2.222-.243-.583-.49-.504-.673-.513l-.573-.01a1.1 1.1 0 0 0-.798.374c-.274.3-1.047 1.023-1.047 2.495 0 1.472 1.072 2.894 1.222 3.094.15.2 2.11 3.222 5.112 4.518.714.309 1.272.493 1.707.631.717.228 1.37.196 1.886.119.575-.086 1.77-.724 2.02-1.423.249-.699.249-1.298.174-1.423-.074-.125-.274-.2-.573-.35Z" />
  </svg>
);

export const waLink = (text) => `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;

// Magnifier lens: attach handlers to a container, ref to the img inside it
export const useMagnifier = (src, zoom = 2.6, size = 140) => {
  const imgRef = useRef(null);
  const [lens, setLens] = useState(null);

  const onMouseMove = (e) => {
    const img = imgRef.current;
    if (!img) return;
    const r = img.getBoundingClientRect();
    const c = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    if (x < 0 || y < 0 || x > r.width || y > r.height) return setLens(null);
    setLens({
      left: e.clientX - c.left - size / 2,
      top: e.clientY - c.top - size / 2,
      bgSize: `${r.width * zoom}px ${r.height * zoom}px`,
      bgPos: `${-(x * zoom - size / 2)}px ${-(y * zoom - size / 2)}px`,
    });
  };

  const lensEl = lens && (
    <div
      data-testid="magnifier-lens"
      className="magnifier-lens"
      style={{
        width: size,
        height: size,
        left: lens.left,
        top: lens.top,
        backgroundImage: `url(${src})`,
        backgroundSize: lens.bgSize,
        backgroundPosition: lens.bgPos,
      }}
    />
  );

  return { imgRef, lensEl, handlers: { onMouseMove, onMouseLeave: () => setLens(null) } };
};

export const TiltCard = ({ children, className = '', max = 7 }) => {
  const ref = useRef(null);
  const move = (e) => {
    const el = ref.current;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `perspective(900px) rotateX(${-py * max}deg) rotateY(${px * max}deg)`;
    el.style.setProperty('--gx', `${(px + 0.5) * 100}%`);
    el.style.setProperty('--gy', `${(py + 0.5) * 100}%`);
  };
  const leave = () => { ref.current.style.transform = 'perspective(900px) rotateX(0) rotateY(0)'; };
  return (
    <div ref={ref} onMouseMove={move} onMouseLeave={leave} className={`tilt-card ${className}`}>
      {children}
    </div>
  );
};

export const Reveal = ({ children, delay = 0, className = '', as: Tag = 'div' }) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } }, { threshold: 0.12 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return (
    <Tag ref={ref} className={`reveal ${shown ? 'reveal-in' : ''} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </Tag>
  );
};

export const ScrollProgress = () => {
  const [p, setP] = useState(0);
  useEffect(() => {
    const on = () => {
      const h = document.documentElement;
      setP((h.scrollTop / Math.max(1, h.scrollHeight - h.clientHeight)) * 100);
    };
    window.addEventListener('scroll', on, { passive: true });
    return () => window.removeEventListener('scroll', on);
  }, []);
  return <div data-testid="scroll-progress" className="scroll-progress" style={{ width: `${p}%` }} />;
};

export const AmbientBubbles = () => {
  const items = React.useMemo(
    () => Array.from({ length: 14 }).map(() => ({
      left: `${Math.random() * 100}%`,
      size: 10 + Math.random() * 38,
      delay: `${Math.random() * 18}s`,
      duration: `${16 + Math.random() * 14}s`,
    })),
    []
  );
  return (
    <div className="fixed inset-0 pointer-events-none z-[5] overflow-hidden" aria-hidden="true">
      {items.map((b, i) => (
        <span key={i} className="ambient-bubble" style={{ left: b.left, width: b.size, height: b.size, animationDelay: b.delay, animationDuration: b.duration }} />
      ))}
    </div>
  );
};
